/**
 * Connectors (§3 `connectors`): the owner's named secrets for outside APIs. Pure checks, shared by
 * `connectors.set`, the manifest's `connectors` field and the builder form.
 */
import { connectorHostProblem } from './connectorFetch';

/** 2–32 chars: a lowercase letter, then lowercase letters, digits and hyphens. */
export const CONNECTOR_NAME_RE = /^[a-z][a-z0-9-]{1,31}$/;
export const MAX_CONNECTORS_PER_MANIFEST = 20;
const MAX_BASE_URL_LENGTH = 500;
const MAX_HEADER_NAME_LENGTH = 64;
const MAX_SECRET_LENGTH = 4096;
/** Hosts that may use plain http (a local echo server in smoke tests and dev). */
const HTTP_HOSTS = new Set(['localhost', '127.0.0.1']);
/** RFC 9110 `token`: what a header field name may be. */
const HEADER_TOKEN_RE = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
/** Framing and hop-by-hop headers: a connector's header must not be able to change how the request is sent. */
const RESERVED_HEADERS = new Set([
  'host',
  'connection',
  'content-length',
  'transfer-encoding',
  'keep-alive',
  'upgrade',
  'te',
  'trailer',
  'expect',
  'proxy-connection',
]);

export type Checked<T> = { ok: true; value: T } | { ok: false; error: string };

export function isValidConnectorName(name: unknown): name is string {
  return typeof name === 'string' && CONNECTOR_NAME_RE.test(name);
}

/**
 * An absolute `https://` URL (plain `http://` only for localhost / 127.0.0.1) to a public host
 * (`connectorHostProblem`: no private IP literals or local names) without credentials,
 * query or fragment, normalised to `<scheme>://<host>[:port][/path]` without a trailing slash, so
 * `baseUrl + '/path'` never needs a slash fixed up.
 */
export function normalizeBaseUrl(input: string): Checked<string> {
  const raw = input.trim();
  const bad = (error: string): Checked<string> => ({ ok: false, error });
  if (raw.length === 0) return bad('Base URL is required');
  if (raw.length > MAX_BASE_URL_LENGTH) return bad(`Base URL must be at most ${MAX_BASE_URL_LENGTH} characters`);
  if (/\s/.test(raw)) return bad('Base URL must not contain spaces');
  if (raw.includes('?') || raw.includes('#')) return bad('Base URL must not have a query (?) or fragment (#); put those in the path of each call');
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return bad('Base URL must be an absolute URL such as https://api.example.com');
  }
  if (url.protocol === 'http:') {
    if (!HTTP_HOSTS.has(url.hostname)) return bad('Base URL must use https:// (plain http:// only for localhost)');
  } else if (url.protocol !== 'https:') {
    return bad('Base URL must use https://');
  }
  if (url.username !== '' || url.password !== '') return bad('Base URL must not contain a user name or password; put the credential in the secret');
  if (url.hostname === '') return bad('Base URL needs a host');
  const hostProblem = connectorHostProblem(`${url.protocol}//${url.host}`);
  if (hostProblem) return bad(`Base URL must be a public host, not ${hostProblem}`);
  const path = url.pathname.replace(/\/+$/, '');
  return { ok: true, value: `${url.protocol}//${url.host}${path}` };
}

/** An HTTP header name (a token), not a framing or hop-by-hop header. Trimmed, case kept. */
export function checkAuthHeader(input: string): Checked<string> {
  const name = input.trim();
  if (name.length === 0) return { ok: false, error: 'Header name is required (e.g. Authorization or X-Api-Key)' };
  if (name.length > MAX_HEADER_NAME_LENGTH) return { ok: false, error: `Header name must be at most ${MAX_HEADER_NAME_LENGTH} characters` };
  if (!HEADER_TOKEN_RE.test(name)) return { ok: false, error: 'Header name may only contain letters, digits and !#$%&\'*+-.^_`|~ (no spaces or colons)' };
  if (RESERVED_HEADERS.has(name.toLowerCase())) return { ok: false, error: `"${name}" cannot carry a credential; use Authorization or an X-… header` };
  return { ok: true, value: name };
}

/**
 * The header value, stored as given apart from surrounding whitespace (which HTTP drops anyway):
 * 1–4096 printable ASCII characters, so it can never smuggle a line break into the request.
 */
export function checkSecret(input: string): Checked<string> {
  const value = input.trim();
  if (value.length === 0) return { ok: false, error: 'Secret is required: paste the full header value (e.g. "Bearer sk-…")' };
  if (value.length > MAX_SECRET_LENGTH) return { ok: false, error: `Secret must be at most ${MAX_SECRET_LENGTH} characters` };
  if (!/^[\t\x20-\x7e]+$/.test(value)) return { ok: false, error: 'Secret may only contain printable ASCII characters (no line breaks)' };
  return { ok: true, value };
}

/**
 * The manifest's `connectors`: an array of connector names, unique, at most 20. Returns the names
 * in the given order, or the problems found.
 */
export function checkManifestConnectors(input: unknown): { ok: true; value: string[] } | { ok: false; errors: string[] } {
  if (!Array.isArray(input)) return { ok: false, errors: ['"connectors" must be an array of connector names, e.g. ["weather"]'] };
  const errors: string[] = [];
  if (input.length > MAX_CONNECTORS_PER_MANIFEST) errors.push(`"connectors" may list at most ${MAX_CONNECTORS_PER_MANIFEST} names`);
  const seen = new Set<string>();
  for (const name of input) {
    if (!isValidConnectorName(name)) {
      errors.push(`connector ${JSON.stringify(name)}: name must match ${CONNECTOR_NAME_RE}`);
    } else if (seen.has(name)) {
      errors.push(`connector "${name}" is listed twice`);
    } else {
      seen.add(name);
    }
  }
  return errors.length > 0 ? { ok: false, errors } : { ok: true, value: [...seen] };
}
